/// <amd-module name="Wit/Common/WorkItemLinksService/CompositeComparer" />
import { IWorkItemTypesService } from "Wit/Common/WorkItemTypesService/IWorkItemTypesService";
import { IBacklogConfiguration } from "Agile/Common/Redux/BacklogConfiguration/BacklogConfigurationContracts";
import {  IResolvedLinkDisplayData } from "Wit/WorkItemLinksRegistration/Contracts";

interface ILinkedArtifactCompareFct<T> {
    (a: T , b: T ): number;
}

interface ILinkedArtifactComparer<T> {
    compare: ILinkedArtifactCompareFct<T>;
}

export class CompositeComparer<T> {
    private _comparers: ILinkedArtifactComparer<T>[] = [];

    /**
     * Add new comparer
     * @param comparer Comparer to add
     */
    public push(comparer: ILinkedArtifactComparer<T>) {
        this._comparers.push(comparer);
    }

    /**
     * Get comparer function that calls all added comparers in sequence
     */
    public getComparer(): ILinkedArtifactCompareFct<T> {
        return (a: T , b: T ): number => {
            for (let comparer of this._comparers) {
                let result = comparer.compare(a, b);
                if (result !== 0) {
                    return result;
                }
            }

            return 0;
        };
    }
}

export enum SortDirection {
    Ascending = 1,
    Descending = -1
}

export class BaseStringComparer<T> implements ILinkedArtifactComparer<T> {
    private _sortDirection: SortDirection;
    private _stringValueExtractor: (item: T) =>  string | undefined

    public constructor(sortDirection: SortDirection, stringValueExtractor: (item: T) =>  string | undefined) {
        this._sortDirection = sortDirection;
        this._stringValueExtractor = stringValueExtractor;
    }

    public compare(a: T , b: T ): number {
        const sortingMethod = sortByStringValue(this._stringValueExtractor);
        const result = sortingMethod(a, b);

        return result * this._sortDirection;
    }
}

export class BaseIntComparer<T> implements ILinkedArtifactComparer<T> {
    private _sortDirection: SortDirection;
    private _numberValueExtractor: (item: T) =>  number | null | undefined

    public constructor(sortDirection: SortDirection, numberValueExtractor: (item: T) =>  number | null | undefined) {
        this._sortDirection = sortDirection;
        this._numberValueExtractor = numberValueExtractor;
    }

    public compare(a: T , b: T ): number {
        const sortingMethod = sortByNumberValue(this._numberValueExtractor);
        const result = sortingMethod(a, b);

        return result * this._sortDirection;
    }
}

function compareNullableStrings(a: string | null | undefined, b: string | null | undefined): number {
    if (!a && !b) {
        return 0;
    }
    if (!a) {
        return 1;
    }
    if (!b) {
        return -1;
    }
    return a.localeCompare(b, navigator.language);
}

function compareNullableNumbers(a: number | null | undefined, b: number | null | undefined): number {
    const isAFalseButNotZero = !a && a !== 0;
    const isBFalseButNotZero = !b && b !== 0;

    if (isAFalseButNotZero && isBFalseButNotZero) {
        return 0;
    }
    if (isAFalseButNotZero) {
        return 1;
    }
    if (isBFalseButNotZero) {
        return -1;
    }
    return a - b;
}

export function sortByStringValue<T>(stringValueExtractor: (item: T | undefined) =>  string | undefined) : ((a: T | undefined, b: T | undefined) => number) {
    return (item1, item2)  => compareNullableStrings(stringValueExtractor(item1), stringValueExtractor(item2));    
}

export function sortByNumberValue<T>(numberValueExtractor: (item: T | null | undefined) =>  number | null | undefined) : ((a: T | undefined, b: T | undefined) => number) {
    return (item1, item2)  => compareNullableNumbers(numberValueExtractor(item1), numberValueExtractor(item2));    
}

export class StateComparer implements ILinkedArtifactComparer<IResolvedLinkDisplayData> {
    private _sortDirection: SortDirection;
    private _backlogConfig: IBacklogConfiguration | undefined;
    private _workItemTypeService: IWorkItemTypesService ;
    private _applyCondition: ((a: IResolvedLinkDisplayData, b: IResolvedLinkDisplayData) => boolean) | undefined;

    public constructor(
        sortDirection: SortDirection, 
        backlogConfig: IBacklogConfiguration | undefined, 
        workItemTypeService: IWorkItemTypesService,
        applyCondition?: (a: IResolvedLinkDisplayData, b: IResolvedLinkDisplayData) => boolean) {
        this._sortDirection = sortDirection;
        this._backlogConfig = backlogConfig;
        this._workItemTypeService = workItemTypeService;
        this._applyCondition = applyCondition;
    }


    public compare(a: IResolvedLinkDisplayData , b: IResolvedLinkDisplayData ): number {
        if (this._applyCondition) {
            const skipApplyingOfComparer = !this._applyCondition(a, b);
            if (skipApplyingOfComparer) {
                return 0;
            }
        }

        let result = 0;

        if (!this._backlogConfig) {
            const sortingMethod = sortByStringValue<IResolvedLinkDisplayData>((linkData) => linkData?.state);
            result = sortingMethod(a, b);
        } else {
            result = this._compare(a, b);
        }

        return result * this._sortDirection;
    }

    public _compare(a: IResolvedLinkDisplayData , b: IResolvedLinkDisplayData ): number {
        if (!a.state && !b.state) {
            return 0;
        }

        if (!a.state) {
            return 1;
        }
        if (!b.state) {
            return -1;
        }

        if (!this._backlogConfig) {
            return 0;
        }
        
        let compareValueA = this._workItemTypeService.getStateCompareValue(a.state, a.linkTypeDisplayName, this._backlogConfig);
        let compareValueB = this._workItemTypeService.getStateCompareValue(b.state, b.linkTypeDisplayName, this._backlogConfig);

        return (compareValueA ?? 0) - (compareValueB ?? 0);
    }
}
